import { apiFetch } from './client';
import type { MetricUnit } from '../components/formatters';
import type { MetricAggregation, OkrDirection, OkrStatus, OkrTaskStatus } from './okrs';

export interface OverviewKeyResult {
  label: string;
  unit: MetricUnit;
  format: string;
  direction: OkrDirection;
  aggregation: MetricAggregation;
  currentValue: number | null;
  currentMonth: string | null;
  currentMonths: string[];
  targetValue: number;
  progressPct: number | null;
}

export interface OverviewOkrSummary {
  id: string;
  quarter: string;
  status: OkrStatus;
  overallProgressPct: number | null;
  keyResults: OverviewKeyResult[];
}

export interface OverviewTaskSummary {
  total: number;
  counts: Partial<Record<OkrTaskStatus, number>>;
}

export interface OverviewMetric {
  layer: string;
  area: string;
  label: string;
  unit: MetricUnit;
  format: string;
  latestActual: number | null;
  latestMonth: string | null;
  mtd: boolean;
  growthPct: number | null;
  trend: { month: string; value: number | null }[];
}

export interface OverviewCampaign {
  key: string;
  label: string;
  currency: string;
  startDate: string;
  endDate: string;
  target: number;
  revenue: number;
  progressPct: number | null;
  started: boolean;
  active: boolean;
  ended: boolean;
  daysTotal: number;
  daysElapsed: number;
  daysRemaining: number;
  orders: number;
  updatedAt: string;
}

export type AdviceSource = 'llm' | 'rule-based';

export interface OverviewAdvice {
  summary: string;
  whatsGoingWell: string[];
  needsImprovement: string[];
  hintsForNextWeek: string[];
  actionItems: string[];
  source: AdviceSource;
  generatedAt: string;
  stale: boolean;
}

export interface OverviewResponse {
  quarter: string;
  okrSummary: OverviewOkrSummary | null;
  taskSummary: OverviewTaskSummary | null;
  headlineMetrics: OverviewMetric[];
  campaign: OverviewCampaign | null;
  advice: OverviewAdvice;
}

export function fetchOverview(refresh?: boolean, signal?: AbortSignal) {
  return apiFetch<OverviewResponse>(`/api/overview${refresh ? '?refresh=1' : ''}`, { signal });
}

export function refreshAdvice() {
  return apiFetch<{ advice: OverviewAdvice }>('/api/overview/advice/refresh', { method: 'POST' });
}
